import RoundedButton from '@/components/ui/custom/buttons/RoundedButton';
import EnumInput from '@/components/ui/custom/dataInput/EnumInput';
import TextAreaInput from '@/components/ui/custom/dataInput/TextAreaInput';
import ToggleButtonGroup from '@/components/ui/custom/dataInput/ToggleGroup';
import { activityGenerator } from '@/data/services/astro.ai/generateServices';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import Lesson from '@/models/Lesson';

import {
  ActivityInput,
  GenerateDifficultyEnum,
  QuestionGenerate,
} from '@/models/astro.ai/generate/Activity';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { useMutation } from '@tanstack/react-query';
import { Control, useController, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Modal from '@/components/ui/custom/modals/Modal';
import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';

type ModalGenerateQuestionProps = {
  onGenerate: (option: QuestionGenerate) => void;
  onClose: () => void;
  visible: boolean;
  lesson: Lesson;
};

type GenerateActivityFields = Omit<ActivityInput, 'lessonId'>;

export default function ModalGenerateQuestion({
  onClose,
  visible,
  ...props
}: ModalGenerateQuestionProps) {
  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'modalGenerateQuestion',
  });

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      testId="modalGenerateQuestion"
      className={{ base: 'gap-3 flex flex-col' }}
    >
      <ConditionalRenderer condition={visible}>
        <Text
          format="poppins-600"
          className="text-primary text-24"
          text={tModal('title')}
        />
        <ModalGenerateQuestionForm onClose={onClose} {...props} />
      </ConditionalRenderer>
    </Modal>
  );
}

const ModalGenerateQuestionForm = ({
  lesson,
  onClose,
  onGenerate,
}: Pick<ModalGenerateQuestionProps, 'lesson' | 'onGenerate' | 'onClose'>) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const { t: tActivity } = useTranslation('translation', {
    keyPrefix: 'activity.manageActivity.activityTypes',
  });
  const { t: tModal } = useTranslation('translation', {
    keyPrefix: 'modalGenerateQuestion',
  });

  const { register, handleSubmit, control } = useForm<GenerateActivityFields>({
    defaultValues: {
      activityType: ActivityTypeEnum.QUIZ,
      numQuestions: 1,
      difficulty: GenerateDifficultyEnum.BEGINNER,
    },
  });

  const { mutate: generate, isLoading } = useMutation(activityGenerator, {
    onSuccess: activity => {
      const question = activity.questions[0];
      if (question) {
        onGenerate(question);
      }
      onClose();
    },
    onError: err => {
      alert.error(getErrorMessage(err));
    },
  });
  const onSubmit = (fields: GenerateActivityFields) => {
    if (fields.activityType !== ActivityTypeEnum.CODE_EDITOR) {
      Reflect.deleteProperty(fields, 'language');
    }

    generate({ ...fields, lessonId: lesson.id });
  };

  const buildToggleButtons = (activities: ActivityTypeEnum[]) => {
    return activities.map(activity => ({
      value: activity,
      text: tActivity(activity),
    }));
  };

  const { field: activityTypeField } = useController({
    control: control,
    name: 'activityType',
  });

  const { field: difficultyField } = useController({
    control: control,
    name: 'difficulty',
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
      <div className="flex justify-center w-full">
        <ToggleButtonGroup
          className={{ base: 'relative mx-auto' }}
          buttons={buildToggleButtons([
            ActivityTypeEnum.QUIZ,
            ActivityTypeEnum.TRUE_OR_FALSE,
            ActivityTypeEnum.CODE_EDITOR,
          ])}
          exclusive
          {...activityTypeField}
        />
      </div>

      <LanguageInput control={control} />

      <TextAreaInput
        {...register('topic')}
        placeholder={tModal('form.topic.placeholder')}
        isOptional
        label={tModal('form.topic.label')}
        labelPosition="top"
      />
      <EnumInput
        placeholder={tModal('form.difficulty.placeholder')}
        enumModel={GenerateDifficultyEnum}
        className={{ label: '!text-16' }}
        displayName={option => t(`difficulty.${option}`)}
        onSelect={difficultyField.onChange}
        selected={difficultyField.value}
        type="string"
        label={tModal('form.difficulty.label')}
        labelPosition="top"
      />
      <div className="flex w-full justify-around gap-6">
        <RoundedButton
          text={t('cancel')}
          color="neutral"
          className="w-full"
          type="button"
          onClick={onClose}
        />
        <RoundedButton
          text={tModal('generate')}
          className="w-full"
          type="submit"
          loading={isLoading}
        />
      </div>
    </form>
  );
};

type LanguageInputProps = {
  control: Control<GenerateActivityFields>;
};
function LanguageInput({ control }: LanguageInputProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const [tCodeEditorLanguage] = useTranslation('translation', {
    keyPrefix: 'codeEditorLanguage',
  });
  const { field: languageField } = useController({
    control,
    name: 'language',
    defaultValue: LanguageCodeEditorEnum.PYTHON,
  });

  const { activityType } = useWatch({ control });

  if (activityType !== ActivityTypeEnum.CODE_EDITOR) return null;

  return (
    <EnumInput
      enumModel={LanguageCodeEditorEnum}
      className={{ label: '!text-16' }}
      displayName={tCodeEditorLanguage}
      onSelect={languageField.onChange}
      selected={languageField.value}
      type="string"
      label={t('form.language.title')}
      labelPosition="top"
    />
  );
}
